/**
 * /app/$tenantSlug/attendance/team — Team Attendance (manager / HR).
 *
 * Date-picker grid of reports (manager) / entity (HR). Scope is enforced
 * server-side in SQL; this page just renders what the API returns (a plain
 * employee with no reports gets an empty list, not a 403).
 *
 * Roster completeness (persona round 2026-10): the day view asks the
 * server for the full roster (?roster=1) — everyone in the caller's scope
 * (a manager's reports, HR's whole company) gets a line, including people
 * who did NOT punch (no_record → muted "No punch" / "Upcoming") and those
 * on a holiday, weekly off, leave or WFH. Scope stays server-side, so a
 * manager never sees beyond their team. (This replaced an HR-only
 * client-side merge with the employee directory.) Durations read as
 * hours and minutes ("8h 05m").
 *
 * Review queue (docs/67 §6): the "Needs review only" switch lists every day
 * of the selected date's month (up to today) that is flagged needs-review
 * and not yet reviewed — GET /attendance/team?from=&to=&needs_review=1,
 * narrowed server-side within the caller's scope — so HR can work through
 * the queue without paging date by date. A reviewed flagged day offers
 * "Withdraw review" (back to the queue).
 */

import { useMemo, useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { createFileRoute } from "@tanstack/react-router";
import { TimeAndLeaveShell } from "@/components/shared/time-and-leave-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/shared/status-badge";
import { EmptyState } from "@/components/shared/empty-state";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { attendanceKeys, useTeamDays } from "@/hooks/use-attendance";
import { useAuth } from "@/lib/auth";
import { isHRAdmin } from "@/lib/nav";
import { isApproverUser } from "@/components/leave/use-leave-roles";
import { attendanceApi } from "@/lib/api";
import { todayISO } from "@/lib/leave-format";
import { downloadBlob } from "@/lib/bulk-import-format";
import {
  emptyDayTitle,
  hoursMinutes,
  monthLabel,
  noPunchLabel,
} from "@/lib/attendance-team";
import {
  BulkReviewDialog,
  MarkReviewedButton,
  UnreviewButton,
} from "@/components/attendance/attendance-review-actions";
import { Switch } from "@/components/ui/switch";
import type { AttendanceDay } from "@/types/attendance";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/attendance/team",
)({
  // A9 hover-prefetch. The page opens on today's (local) date — the same
  // deterministic todayISO() the component seeds its date state with, so
  // the loader warms exactly the first render's cache entry. Server-side
  // scope means this is safe for any caller (no 403, just an empty list).
  loader: ({ context }) => {
    const date = todayISO();
    return context.queryClient
      .ensureQueryData({
        queryKey: attendanceKeys.team({ date, roster: 1 }),
        queryFn: ({ signal }) =>
          attendanceApi.listTeamDays({ date, roster: 1 }, signal),
      })
      .catch(() => undefined);
  },
  component: TeamAttendanceRoute,
});

/** "2026-07-16" → "Wed, 16 Jul 2026" (falls back to the raw string). */
function fmtDayTitle(iso: string): string {
  const d = new Date(`${iso}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  // en-GB renders "Wed, 16 Jul 2026" — en-IN inserts a comma before the
  // year ("16 Jul, 2026").
  return d.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

/** The review-queue window for a date: its month, up to today. */
function queueRange(date: string, today: string): { from: string; to: string } {
  const from = `${date.slice(0, 7)}-01`;
  const [y, m] = [Number(date.slice(0, 4)), Number(date.slice(5, 7))];
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const end = `${date.slice(0, 7)}-${String(last).padStart(2, "0")}`;
  return { from, to: end < today ? end : today < from ? from : today };
}

/** One rendered roster row: an attendance day, or a no-punch employee. */
interface RosterRow {
  key: string;
  name: string;
  day: AttendanceDay | null;
}

function TeamAttendanceRoute() {
  // Default to *today* (local). Using toISOString() here returned the UTC
  // date, which for IST users showed yesterday once the local day rolled over.
  const [date, setDate] = useState(() => todayISO());
  // Review queue: flagged-and-unreviewed days of the selected month.
  const [queue, setQueue] = useState(false);
  const range = queueRange(date || todayISO(), todayISO());
  // The day view asks for the full roster (roster=1): everyone in scope,
  // including people who did not punch. The review queue lists flagged
  // days only — a "No punch" line is nothing to review.
  const team = useTeamDays(
    queue ? { ...range, needs_review: 1 } : { date, roster: 1 },
  );
  const { user } = useAuth();

  const hrAdmin = isHRAdmin(user?.role);
  // The export is for HR (everyone) and people with a team (their reports).
  // F164: it follows approver STANDING like the server (manager role, a
  // team lead with reports, a live delegate), not the role alone.
  const canExport = hrAdmin || isApproverUser(user);
  const [exporting, setExporting] = useState(false);
  const month = date.slice(0, 7); // YYYY-MM of the selected date

  async function onExport() {
    setExporting(true);
    try {
      const blob = await attendanceApi.exportMonth(month);
      downloadBlob(blob, `attendance-${month}.csv`);
    } catch {
      toast.error("Could not export attendance");
    } finally {
      setExporting(false);
    }
  }
  const rows = useMemo<RosterRow[]>(() => {
    const days = team.data?.days ?? [];
    const out: RosterRow[] = days.map((d) => ({
      key: `${d.employee_id}-${d.work_date}`,
      name: d.user_name ?? `Employee #${d.employee_id}`,
      // A no-record line (?roster=1) renders as the muted "No punch" row.
      day: d.no_record ? null : d,
    }));
    // The day view reads like a register: alphabetical. The review queue
    // keeps the server's newest-first date order.
    if (!queue) out.sort((a, b) => a.name.localeCompare(b.name));
    return out;
  }, [team.data, queue]);

  return (
    <TimeAndLeaveShell
      title="Today"
      description="Who was present, and for how long."
      actions={
        <div className="flex min-w-0 max-w-full flex-wrap items-end gap-2">
          <div>
            <Label htmlFor="date">Date</Label>
            <Input
              id="date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-32"
            />
          </div>
          {canExport && (
            <Button
              variant="outline"
              className="gap-1.5"
              onClick={onExport}
              disabled={exporting || !date}
              title={`Download ${monthLabel(month)} as CSV`}
            >
              {exporting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              Export {monthLabel(month)}
            </Button>
          )}
          {hrAdmin && <BulkReviewDialog />}
          <div className="relative flex items-center gap-2 pb-2 pointer-coarse:min-h-11 pointer-coarse:-mx-3.5 pointer-coarse:px-3.5 pointer-coarse:pb-0">
            <Switch
              id="review-queue"
              checked={queue}
              onCheckedChange={setQueue}
            />
            <Label htmlFor="review-queue" className="text-sm font-normal after:absolute after:inset-0 after:content-['']">
              Needs Review Only
            </Label>
          </div>
        </div>
      }
    >
      <Card>
        <CardHeader>
          <CardTitle>
            {queue
              ? `Needs Review — ${fmtDayTitle(range.from)} to ${fmtDayTitle(range.to)}`
              : fmtDayTitle(date)}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {team.isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : rows.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  {queue && <TableHead>Date</TableHead>}
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Worked</TableHead>
                  <TableHead className="text-right">Late By</TableHead>
                  <TableHead className="text-right">Overtime</TableHead>
                  {hrAdmin && <TableHead className="text-right">Review</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) =>
                  r.day ? (
                    <TableRow key={r.key}>
                      <TableCell>{r.name}</TableCell>
                      {queue && <TableCell>{fmtDayTitle(r.day.work_date)}</TableCell>}
                      <TableCell>
                        <StatusBadge status={r.day.status} />
                        {r.day.is_regularized && (
                          <Badge variant="secondary" className="ml-1">
                            regularized
                          </Badge>
                        )}
                        {r.day.needs_review && r.day.reviewed_at && (
                          <Badge
                            variant="secondary"
                            className="ml-1"
                            title={r.day.review_note ?? "Reviewed by HR"}
                          >
                            reviewed
                          </Badge>
                        )}
                        {r.day.needs_review && !r.day.reviewed_at && (
                          <Badge
                            variant="outline"
                            className="ml-1 border-amber-500 text-amber-700"
                            title="Includes a punch synced from offline with a clock far from the server's — please review"
                          >
                            needs review
                          </Badge>
                        )}
                        {r.day.long_session && !(r.day.needs_review && !r.day.reviewed_at) && (
                          <Badge
                            variant="outline"
                            className="ml-1 border-amber-500 text-amber-700"
                            title="More than 16 hours between punch-in and punch-out — the punch-out was probably missed. Correct the day with a regularization if it's wrong."
                            data-testid="attendance-long-session"
                          >
                            needs review
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {hoursMinutes(r.day.worked_minutes)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {hoursMinutes(r.day.late_minutes)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {hoursMinutes(r.day.overtime_minutes)}
                      </TableCell>
                      {hrAdmin && (
                        <TableCell className="text-right">
                          {r.day.needs_review && !r.day.reviewed_at && (
                            <MarkReviewedButton day={r.day} />
                          )}
                          {r.day.needs_review && r.day.reviewed_at && (
                            <UnreviewButton day={r.day} />
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ) : (
                    <TableRow key={r.key} className="text-muted-foreground">
                      <TableCell>{r.name}</TableCell>
                      {queue && <TableCell />}
                      {/* A future date has no punches YET — "No punch"
                          read as an absence (2026-10 re-test). */}
                      <TableCell>{noPunchLabel(date, todayISO())}</TableCell>
                      <TableCell className="text-right">—</TableCell>
                      <TableCell className="text-right">—</TableCell>
                      <TableCell className="text-right">—</TableCell>
                      {hrAdmin && <TableCell />}
                    </TableRow>
                  ),
                )}
              </TableBody>
            </Table>
          ) : queue ? (
            <EmptyState title="Nothing waiting for review this month." />
          ) : (
            <EmptyState title={emptyDayTitle(date, todayISO())} />
          )}
        </CardContent>
      </Card>
    </TimeAndLeaveShell>
  );
}
